import { Field, Label, Suggestion } from '@innovationnorway/cl-react';
import React, { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BrRegEnhet } from 'src/api/brreg';
import { useBrregSearch } from 'src/hooks/use-brreg-search';
import { useDebounce } from 'usehooks-ts';

export type BrregCompany = {
  organizationNumber: string;
  name: string;
  address: string;
};

type Props = {
  label: string;
  disabled?: boolean;
  inputRef?: React.Ref<HTMLInputElement>;
  className?: string;
  onSelect: (company: Pick<BrregCompany, 'organizationNumber' | 'name'>) => void;
  onClearSelection?: () => void;
};

const mapBrregCompany = (company: BrRegEnhet): BrregCompany => ({
  organizationNumber: company.organisasjonsnummer,
  name: company.navn,
  address: company.forretningsadresse
    ? `${company.forretningsadresse.postnummer ?? ''} ${company.forretningsadresse.kommune ?? ''}`
    : '',
});

const BrregCompanySearch: React.FC<Props> = ({
  label,
  disabled,
  inputRef,
  className,
  onSelect,
  onClearSelection,
}) => {
  const { t: tCommon } = useTranslation();
  const inputId = useId();
  const [searchValue, setSearchValue] = useState('');
  const debouncedSearchValue = useDebounce(searchValue, 100);
  const { data } = useBrregSearch(debouncedSearchValue);
  const companies = data?._embedded?.enheter.map(mapBrregCompany) ?? [];

  const handleSelect = (organizationNumber: string) => {
    const company = companies.find((item) => item.organizationNumber === organizationNumber);
    if (!company) return;

    setSearchValue('');
    onSelect({ organizationNumber: company.organizationNumber, name: company.name });
  };

  return (
    <div className={className}>
      <Field>
        <Label htmlFor={inputId}>{label}</Label>
        <Suggestion
          className="brreg-company-search"
          filter={false}
          selected={null}
          onSelectedChange={(item) => {
            if (item) {
              handleSelect(item.value);
            } else {
              setSearchValue('');
            }
          }}
        >
          <Suggestion.Input
            id={inputId}
            ref={inputRef}
            className="brreg-company-search__input"
            disabled={disabled}
            onInput={(event) => {
              const value = event.currentTarget.value;
              if (value && !searchValue) onClearSelection?.();
              setSearchValue(value);
            }}
          />
          <Suggestion.Clear
            className="brreg-company-search__clear"
            onClick={() => onClearSelection?.()}
          />
          <Suggestion.List>
            <Suggestion.Empty>{tCommon('noHits')}</Suggestion.Empty>
            {companies.map((company) => {
              const organizationNumberLabel = `${tCommon('organizationNumber')} ${company.organizationNumber}`;
              const label = `${company.name}, ${organizationNumberLabel}`;
              return (
                <Suggestion.Option key={company.organizationNumber} label={label} value={company.organizationNumber}>
                  <span>
                    <strong>{company.name}</strong>
                    <span>{organizationNumberLabel}</span>
                    {company.address && <small>{company.address}</small>}
                  </span>
                </Suggestion.Option>
              );
            })}
          </Suggestion.List>
        </Suggestion>
      </Field>
    </div>
  );
};

export default BrregCompanySearch;